import { senecFPPTheme } from "src/styles/fpp-theme/senecFppTheme";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";
import { useScreenSize } from "src/hooks/useScreenSize";
import { ThemeProvider } from "@mui/system";
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Tooltip, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ReactElement, useMemo, useState } from "react";
import { Accept, ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import {
    filterDuplicateFiles,
    sumFileSizes,
    toByteConverter,
    toSizeStringConverter,
} from "src/components/inputs/file-upload/fileUploadUtils";
import { getAcceptedTypesFileExtensions } from "src/components/inputs/file-upload/fileUploadAcceptedTypes";
import { InvalidFilesSnackbar, InvalidFilesErrorMessages } from "src/components/inputs/file-upload/InvalidFilesSnackBar";
import { FilePreview } from "src/components/inputs/file-upload/FilePreview";
import { ChevronDownIcon, UploadIcon } from "src/foundations/icons";

/**
 * acceptedFileTypes:
 * - types accepted by the file upload
 * - given as "mimeType": [".fileExtension"] => Accept object
 * - e.g. { "image/jpeg": [".jpg"] }
 *
 * maxFileSize:
 * - given as "{value} {unit}" string
 * - defaults to "10 MB"
 * - supported units: KiB, MiB, GiB, TiB, KB, MB, GB, TB
 * - !!! The cluster nginx currently only supports requests with "25 MB"
 *
 * required:
 * - boolean indicating if file selection is required
 *
 * error:
 * - error of the file upload (e.g. if form validation fails
 *   due to missing, but required, value)
 *
 * value:
 * - current state of the file upload
 *
 * onChange:
 * - function to set the value state of the file upload
 *
 * labels:
 * - object of UI texts for the file upload
 */
export interface FileUploadProps {
    acceptedFileTypes?: Accept;
    maxFileSize?: string;
    required?: boolean;
    hasError?: boolean;
    allowFileDeletion?: boolean;
    value: File[];
    onChange: (file: File[]) => void;
    labels: {
        noFileChosen: string;
        maxFileSizeLeft: string;
        maxFileSize: string;
        supportedFileExt: string;
        message: string;
        mobileMessage: string;
        errorMessages: InvalidFilesErrorMessages;
    };
}

export const fileUploadDefaultLabels = {
    noFileChosen: "No file chosen",
    maxFileSizeLeft: "{{remainingFileSize}} remaining",
    maxFileSize: " of {{maxFileSize}}",
    supportedFileExt: "Supported file types",
    message: "Click to select files,\nor drag and drop files",
    mobileMessage: "Click to select files",
    errorMessages: {
        [ErrorCode.FileInvalidType]: "Invalid file type!",
        [ErrorCode.FileTooLarge]: "Max upload size exceeded!",
        [ErrorCode.FileTooSmall]: "Invalid file selection!",
        [ErrorCode.TooManyFiles]: "Invalid file selection!",
        unknown: "Invalid file selection!",
    },
};

/**
 * `SenecSearchbar` is a multi-field upload field that supports drag-and-drop.
 */
export const FileUpload = ({
    acceptedFileTypes,
    maxFileSize = "10 MB",
    required = false,
    hasError,
    value = [],
    onChange,
    allowFileDeletion = true,
    labels,
}: FileUploadProps) => {
    const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);

    const onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        // remove duplicate files
        const acceptedFilesWithoutDupes = filterDuplicateFiles(acceptedFiles, value);

        // reject files exceeding max file size
        const remainingFileSize = getRemainingFileSize();
        if (sumFileSizes(acceptedFilesWithoutDupes) > remainingFileSize) {
            const sizeAcceptedFiles: File[] = [];
            const sizeRejectedFiles: FileRejection[] = [];
            let sizeLeft = remainingFileSize;

            for (const file of acceptedFilesWithoutDupes) {
                if (sizeLeft >= file.size) {
                    sizeAcceptedFiles.push(file);
                    sizeLeft -= file.size;
                } else {
                    sizeRejectedFiles.push({
                        file: file,
                        errors: [
                            {
                                message: `Maximum file size exceeded. File is larger than ${remainingFileSize} bytes`,
                                code: ErrorCode.FileTooLarge,
                            },
                        ],
                    });
                }
            }

            onChange([...value, ...sizeAcceptedFiles]);
            setRejectedFiles([...rejectedFiles, ...sizeRejectedFiles]);
            return;
        }

        onChange([...value, ...acceptedFilesWithoutDupes]);
        setRejectedFiles(rejectedFiles);
    };

    const deleteSelectedFile = (file: File) => {
        onChange(value.filter((fileToUpload) => fileToUpload !== file));
    };

    const clearRejectedFiles = () => {
        setRejectedFiles([]);
    };

    const getRemainingFileSize = () => toByteConverter(maxFileSize) - sumFileSizes(value);

    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        accept: acceptedFileTypes,
        maxSize: getRemainingFileSize(),
        onDrop,
    });
    const dropZoneStyle = useDropZoneStyle(isFocused, isDragAccept, isDragReject);

    return (
        /*
    The file upload component explicitly imports the `ThemeProvider` from `@mui/systems`.
    That's why we have to set it explicitly.
     */
        <ThemeProvider theme={senecFPPTheme}>
            <Stack spacing={1}>
                <FileUploadErrorTooltip
                    hasError={hasError}
                    labels={{ noFileChosen: labels.noFileChosen, supportedFileExt: labels.supportedFileExt }}
                >
                    <Box {...getRootProps(dropZoneStyle)}>
                        <input {...getInputProps()} data-testid="file-upload" />
                        <UploadFilePrompt
                            labels={{ message: labels.message, mobileMessage: labels.mobileMessage }}
                            required={required}
                        />
                    </Box>
                </FileUploadErrorTooltip>
                <FileUploadHelpSection
                    acceptedFileTypes={acceptedFileTypes}
                    maxFileSize={maxFileSize}
                    remainingFileSize={getRemainingFileSize()}
                    labels={{
                        maxFileSizeLeft: labels.maxFileSizeLeft,
                        maxFileSize: labels.maxFileSize,
                        supportedFileExt: labels.supportedFileExt,
                    }}
                />
                <FilePreview files={value} onDeleteFile={allowFileDeletion ? deleteSelectedFile : undefined} />
                <InvalidFilesSnackbar
                    rejectedFiles={rejectedFiles}
                    clearRejectedFiles={clearRejectedFiles}
                    errorMessages={labels.errorMessages}
                />
            </Stack>
        </ThemeProvider>
    );
};

export const FileUploadErrorTooltip = ({
    hasError,
    labels,
    children,
}: {
    hasError?: boolean;
    labels: { noFileChosen: string; supportedFileExt: string };
    children: ReactElement;
}) => {
    const theme: SenecFPPTheme = useTheme();

    return (
        <Tooltip
            data-testid="file-upload-error-tooltip"
            title={labels.noFileChosen}
            open={hasError}
            arrow
            PopperProps={{
                sx: {
                    "& .MuiTooltip-tooltip": {
                        boxShadow: theme.shadows[1],
                        border: `1px solid ${theme.palette.black.main}`,
                        backgroundColor: theme.palette.white.main,
                        color: theme.palette.neutralGray100.main,
                        fontSize: "inherit",
                        padding: "0.5rem",
                    },
                    "& .MuiTooltip-arrow": {
                        left: "-40% !important",
                        "&:before": {
                            border: `1px solid ${theme.palette.black.main}`,
                            backgroundColor: theme.palette.white.main,
                            color: theme.palette.neutralGray100.main,
                        },
                    },
                },
            }}
        >
            {children}
        </Tooltip>
    );
};

export const FileUploadHelpSection = ({
    acceptedFileTypes,
    maxFileSize,
    remainingFileSize,
    labels,
}: {
    acceptedFileTypes?: Accept;
    maxFileSize: string;
    remainingFileSize: number;
    labels: {
        maxFileSizeLeft: string;
        maxFileSize: string;
        supportedFileExt: string;
    };
}) => {
    const theme: SenecFPPTheme = useTheme();

    const sideNoteStyle = {
        color: theme.palette.neutralGray50.main,
        fontSize: "0.75rem !important",
    };

    return (
        <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="flex-start"
            my={2}
            data-testid="file-upload-help-section"
        >
            <Typography sx={sideNoteStyle}>
                <Typography
                    component="span"
                    sx={{
                        ...sideNoteStyle,
                        color: theme.palette.deepBlue.main,
                        fontWeight: "bold",
                    }}
                >
                    {labels.maxFileSizeLeft.replace("{{remainingFileSize}}", toSizeStringConverter(remainingFileSize))}
                </Typography>
                {labels.maxFileSize.replace("{{maxFileSize}}", maxFileSize)}
            </Typography>

            {acceptedFileTypes && (
                <Accordion
                    disableGutters
                    sx={{
                        backgroundColor: "transparent",
                        border: "none",
                        boxShadow: "none",
                        p: 0,
                        m: 0,
                        width: "50%",
                    }}
                >
                    <AccordionSummary
                        sx={{
                            m: 0,
                            p: 0,
                            minHeight: 0,
                            "& .MuiAccordionSummary-content": {
                                margin: 0,
                            },
                            flexDirection: { xs: "row-reverse", sm: "row" },
                        }}
                        expandIcon={
                            <ChevronDownIcon
                                sx={{
                                    color: theme.palette.neutralGray50.main,
                                    fontSize: "1rem",
                                }}
                            />
                        }
                    >
                        <Typography
                            sx={{
                                ...sideNoteStyle,
                                width: "100%",
                                textAlign: { sm: "right" },
                            }}
                        >
                            {labels.supportedFileExt}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ m: 0, p: 0 }}>
                        <Typography
                            sx={{
                                ...sideNoteStyle,
                                width: "100%",
                                textAlign: { sm: "right" },
                            }}
                        >
                            {getAcceptedTypesFileExtensions(acceptedFileTypes)}
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            )}
        </Stack>
    );
};

export const FileUploadErrorCode = ErrorCode;

export const UploadFilePrompt = ({
    labels,
    required,
}: {
    labels: { message: string; mobileMessage: string };
    required: boolean;
}) => {
    const { isMobile } = useScreenSize();
    return (
        <Stack justifyContent="center" alignItems="center" data-testid="file-upload-upload-file-prompt">
            <UploadIcon color="primary" />
            <Typography sx={{ whiteSpace: "pre-line", textAlign: "center" }}>
                {isMobile ? labels.mobileMessage : labels.message}
                {required ? " *" : ""}
            </Typography>
        </Stack>
    );
};

const useDropZoneStyle = (isFocused: boolean, isDragAccept: boolean, isDragReject: boolean) => {
    const theme: SenecFPPTheme = useTheme();
    return useMemo(() => {
        const baseStyle = {
            padding: "1.5rem 1rem",
            minHeight: "5rem",
            border: "2px dashed",
            borderRadius: "0.75rem",
            borderColor: theme.palette.neutralGray20.main,
            backgroundColor: theme.palette.neutralGray000.main,
        };

        const baseInteractionStyle = {
            // compensate missing 1px border width with padding
            padding: "calc(1px + 1.5rem) calc(1px + 1rem)",
            border: "1px solid",
            borderColor: theme.palette.deepBlue.main,
            backgroundColor: theme.palette.white.main,
            cursor: "pointer",
        };

        return {
            sx: {
                "&:hover": baseInteractionStyle,
            },
            ...baseStyle,
            ...(isFocused ? baseInteractionStyle : {}),
            ...(isDragAccept ? baseInteractionStyle : {}),
            ...(isDragReject ? baseInteractionStyle : {}),
        };
    }, [isFocused, isDragAccept, isDragReject, theme]);
};
